import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { message, Modal, Button } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useAttendance } from '@/context/AttendanceContext';
import {
    fetchTaskById,
    fetchTaskActivityLog,
    fetchTaskTimerState,
    addComment,
    deleteComment,
    deleteAttachment,
    bulkDeleteAttachments,
    createTask,
    updateTask,
    updateTaskStatus,
    addSubtask,
    updateSubtask,
    deleteSubtask,
    generateShareLink,
    revokeShareLink,
    improveTaskDescription,
    uploadAttachment,
    fetchTasks,
    getDownloadUrl,
    getAttachmentPreviewUrl,
    toggleCommentReaction,
} from '../api/projectManagementApi';
import { getFullName } from './utils/personUtils';
import { connectSocket, releaseSocket } from '@/services/socketClient';
import { buildEstimatedTimeString, getRunningTaskSegmentMs, hmToMs, msToHM, parseDurationStringToMs, parseEstimatedMs, parseEstimatedTimeString } from './utils/timeFormatters';
import { PRIORITY_COLORS } from './constants';
import {
    createPendingAttachment,
    cleanupPendingAttachment,
    validateAttachmentFile,
    setOptimisticImageUrl,
    deleteOptimisticImageUrl,
    isImageFile,
    isQuillEmpty,
} from '../utils/attachmentUtils';

const TaskDrawerContext = createContext(null);

let cachedAllTasks = null;
let lastTasksFetch = 0;

export function TaskDrawerProvider({ children, value }) {
    const {
        taskId,
        open,
        onClose,
        closeAll,
        currentAdminId,
        currentAdmin,
        onTaskUpdated,
        employees = [],
        projects = [],
        pushTask,
        popTask,
        popTo,
        updateStackItem,
        drawerStack,
        isTop,
        hasMask,
        mode = 'view',
        parentId = null,
        initialMilestoneId = null,
        selectedProject = null,
        selectedAssignee = null,
        defaultStatus = 'Backlog',
        onSuccess,
        workspaceTasks = [],
    } = value;

    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [actualTimeMs, setActualTimeMs] = useState(0);
    const [subtasks, setSubtasks] = useState([]);
    const [collaborators, setCollaborators] = useState([]);
    const [reviewers, setReviewers] = useState([]);
    const [additionalProjects, setAdditionalProjects] = useState([]);
    const [milestoneId, setMilestoneId] = useState(null);
    const [commentText, setCommentText] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [commentAttachments, setCommentAttachments] = useState([]); // pending files attached to a comment
    const [commentFileReferences, setCommentFileReferences] = useState([]); // referenced StorageFiles (@File mention)
    const [commentMentions, setCommentMentions] = useState([]); // referenced Users (@User mention)
    const [isUploadingDescription, setIsUploadingDescription] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState([]); // pending files in the Attachments tab
    const [attachmentsDragOver, setAttachmentsDragOver] = useState(false);
    const [activeTab, setActiveTab] = useState('comments');
    const [isBottomCollapsed, setIsBottomCollapsed] = useState(false);
    const [isBottomExpanded, setIsBottomExpanded] = useState(false);
    const [addingSubtask, setAddingSubtask] = useState(false);
    const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
    const [savingSubtask, setSavingSubtask] = useState(false);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [sharingLoading, setSharingLoading] = useState(false);
    const [shareUrl, setShareUrl] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editPriority, setEditPriority] = useState('Medium');
    const [editEstHours, setEditEstHours] = useState(0);
    const [editEstMinutes, setEditEstMinutes] = useState(0);
    const [editEffortHours, setEditEffortHours] = useState(0);
    const [editEffortMinutes, setEditEffortMinutes] = useState(0);
    const [itemType, setItemType] = useState('task'); // 'task' | 'milestone'
    const [milestoneOwner, setMilestoneOwner] = useState(null);
    const [completionCriteria, setCompletionCriteria] = useState('');
    const [deliverables, setDeliverables] = useState([]);
    const [initialChildTasks, setInitialChildTasks] = useState([]);
    const [isMilestoneModalOpen, setIsMilestoneModalOpen] = useState(false);
    const [updatingTask, setUpdatingTask] = useState(false);
    const [improvingDescription, setImprovingDescription] = useState(false);
    const [preImproveDescription, setPreImproveDescription] = useState(null); // snapshot for undo
    const [expandedProjects, setExpandedProjects] = useState({});
    const [externalRunningTasks, setExternalRunningTasks] = useState({});
    const [projectTasks, setProjectTasks] = useState([]);
    const [liveTick, setLiveTick] = useState(Date.now());
    const commentEndRef = useRef(null);
    // Snapshot of the last-saved values — used to detect unsaved changes on close
    const savedSnapshotRef = useRef(null);
    const { attendance, status: attendanceStatus, startTask, stopTask, refreshSession } = useAttendance() || {};

    // ── Auto-save debounce refs ──────────────────────────────────────────────────
    // Fields that used to require the Update Task button are now saved automatically
    // 1800ms after the user stops typing/changing. Only fires in view mode (not create or draft).
    const autoSaveTimerRef = useRef(null);
    const deletedAttachmentIdsRef = useRef(new Set());
    const inFlightTaskIdRef = useRef(null);
    // Keyed by entry.id → AbortController; guaranteed to exist before state batch runs
    const uploadAbortControllersRef = useRef(new Map());
    // Prevents auto-save from firing during task fetch and initial load
    const isInitialMountOrLoadingRef = useRef(true);
    const [isAutoSaving, setIsAutoSaving] = useState(false);

    const scheduleAutoSave = useCallback((payload) => {
        const isDraft = Boolean(task?.isDraft || task?.status === 'Draft');
        if (isInitialMountOrLoadingRef.current || loading || mode !== 'view' || !taskId || taskId === 'new' || isDraft) return;
        // Never persist blob: URLs or upload-placeholder content to MongoDB
        if (typeof payload?.description === 'string' && (
            /src=["']blob:|href=["']blob:/i.test(payload.description) ||
            payload.description.includes('uploading.placeholder') ||
            payload.description.includes('data-up%3D%22')
        )) {
            return;
        }
        // Skip scheduling if only trailing whitespace or empty paragraph changes.
        // normalizeDesc: collapse &nbsp;, strip spaces before closing tags, collapse whitespace, strip trailing empty paras
        if (typeof payload?.description === 'string') {
            const normalizeDesc = (html) => (html || '')
                .replace(/&nbsp;/gi, ' ')
                .replace(/ +(<\/[a-z]+>)/gi, '$1')
                .replace(/\s+/g, ' ')
                .replace(/(?:<p>\s*<br\s*\/?>\s*<\/p>\s*)+$/gi, '')
                .trim();
            const currentSnapDesc = savedSnapshotRef.current?.description ?? task?.description ?? '';
            if (normalizeDesc(payload.description) === normalizeDesc(currentSnapDesc)) {
                return;
            }
        }
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = setTimeout(async () => {
            setIsAutoSaving(true);
            try {
                const { data } = await updateTask(taskId, payload);
                if (data?.result) {
                    cachedAllTasks = null;
                    setTask((prev) => {
                        if (!prev) return prev;
                        const next = { ...prev, ...data.result };
                        // Never let an in-flight metadata/description auto-save overwrite live attachments
                        // or resurrect attachments that were deleted while the request was in flight
                        const sourceAttachments = payload.attachments !== undefined
                            ? (data.result.attachments || [])
                            : (prev.attachments || data.result.attachments || []);
                        next.attachments = sourceAttachments.filter(
                            (a) => !deletedAttachmentIdsRef.current.has(String(a?._id))
                        );
                        if (payload.comments === undefined && prev.comments) {
                            next.comments = prev.comments;
                        }
                        return next;
                    });
                    onTaskUpdated?.(data.result);
                    // Keep snapshot in sync so dirty-check stays accurate
                    savedSnapshotRef.current = {
                        title: data.result.title ?? editTitle,
                        description: data.result.description ?? editDescription,
                        priority: data.result.priority ?? editPriority,
                        estH: editEstHours,
                        estM: editEstMinutes,
                    };
                }
            } catch {
                // Silent — don't interrupt the user with an error toast for background saves
            } finally {
                setIsAutoSaving(false);
            }
        }, 1800);
    }, [mode, taskId, task?.isDraft, task?.status, onTaskUpdated, editTitle, editDescription, editPriority, editEstHours, editEstMinutes]);

    // Auto-save: title (only for published tasks in view mode, never for drafts or new tasks)
    useEffect(() => {
        if (isInitialMountOrLoadingRef.current || loading || mode !== 'view' || task?.isDraft || task?.status === 'Draft') return;
        if (editTitle.trim() && editTitle !== (savedSnapshotRef.current?.title ?? task?.title)) {
            scheduleAutoSave({ title: editTitle });
        }
    }, [editTitle, task?.isDraft, task?.status, mode, scheduleAutoSave]);

    // Auto-save: description (only for published tasks in view mode, never for drafts or new tasks)
    useEffect(() => {
        if (isInitialMountOrLoadingRef.current || loading || mode !== 'view' || task?.isDraft || task?.status === 'Draft') return;
        // Never schedule a save while blob: or upload-placeholder URLs are in the description
        if (typeof editDescription === 'string' && (
            /src=["']blob:|href=["']blob:/i.test(editDescription) ||
            editDescription.includes('uploading.placeholder') ||
            editDescription.includes('data-up%3D%22')
        )) return;
        if (editDescription !== (savedSnapshotRef.current?.description ?? task?.description)) {
            scheduleAutoSave({ description: editDescription });
        }
    }, [editDescription, task?.isDraft, task?.status, mode, scheduleAutoSave]);

    // Auto-save: priority (only for published tasks in view mode, never for drafts or new tasks)
    useEffect(() => {
        if (isInitialMountOrLoadingRef.current || loading || mode !== 'view' || task?.isDraft || task?.status === 'Draft') return;
        if (editPriority && editPriority !== (savedSnapshotRef.current?.priority ?? task?.priority)) {
            scheduleAutoSave({ priority: editPriority });
        }
    }, [editPriority, task?.isDraft, task?.status, mode, scheduleAutoSave]);

    // Auto-save: estimated time (only for published tasks in view mode, never for drafts or new tasks)
    useEffect(() => {
        if (isInitialMountOrLoadingRef.current || loading || mode !== 'view' || task?.isDraft || task?.status === 'Draft') return;
        const snap = savedSnapshotRef.current;
        if (editEstHours !== (snap?.estH ?? 0) || editEstMinutes !== (snap?.estM ?? 0)) {
            const estimatedTime = buildEstimatedTimeString(editEstHours, editEstMinutes);
            scheduleAutoSave({ estimatedTime });
        }
    }, [editEstHours, editEstMinutes, task?.isDraft, task?.status, mode, scheduleAutoSave]);

    // Cleanup timer on unmount
    useEffect(() => () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); }, []);

    // ── Clean up any legacy localStorage draft keys ────────────────────────────
    const clearTaskDraft = useCallback((specificProjId = null) => {
        try {
            if (specificProjId && specificProjId !== 'all') {
                localStorage.removeItem(`pm_task_draft_${specificProjId}`);
            }
            const currentProjId = task?.project?._id || (typeof selectedProject === 'object' ? selectedProject?._id : selectedProject);
            if (currentProjId && currentProjId !== 'all') {
                localStorage.removeItem(`pm_task_draft_${currentProjId}`);
            }
            localStorage.removeItem('pm_task_draft_latest');
            localStorage.removeItem('pm_task_draft_default');
            localStorage.removeItem('pm_task_draft_all');
        } catch { /* ignore */ }
    }, [task?.project, selectedProject]);

    const handleAddInitialChildTask = (title) => {
        if (!title.trim()) return;
        setInitialChildTasks((prev) => [...prev, title.trim()]);
    };

    const handleRemoveInitialChildTask = (index) => {
        setInitialChildTasks((prev) => prev.filter((_, idx) => idx !== index));
    };

    const handleAddInitialMilestoneTask = handleAddInitialChildTask;
    const handleRemoveInitialMilestoneTask = handleRemoveInitialChildTask;

    const loadTask = useCallback(async () => {
        isInitialMountOrLoadingRef.current = true;
        if (!taskId && mode !== 'create') return;
        if (mode !== 'create' && inFlightTaskIdRef.current === taskId) return;
        inFlightTaskIdRef.current = taskId;
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            setIsAutoSaving(false);
        }
        setLoading(true);
        setLoadError(false);
        try {
            if (mode === 'create') {
                // Clean up any leftover browser storage drafts from older versions
                clearTaskDraft();

                // Resolve project (do NOT default to projects[0] unless explicitly in that project view)
                const resolvedProjId = (selectedProject && selectedProject !== 'all')
                    ? (typeof selectedProject === 'object' ? selectedProject._id : selectedProject)
                    : null;

                const projObj = resolvedProjId
                    ? (projects?.find(p => String(p._id) === String(resolvedProjId)) || (typeof selectedProject === 'object' && selectedProject?._id ? selectedProject : null))
                    : null;
                const defaultPm = projObj?.projectManager;
                const initialMsId = projObj ? (initialMilestoneId || ((parentId && parentId !== 'new') ? parentId : null)) : null;
                const resolvedAssignee = selectedAssignee
                    ? (typeof selectedAssignee === 'object' ? selectedAssignee : { _id: selectedAssignee })
                    : null;

                // Initialize a fresh blank mock task for creation
                const mockTask = {
                    _id: 'new',
                    title: '',
                    description: '',
                    itemType: itemType || 'task',
                    priority: 'Medium',
                    status: defaultStatus || 'Backlog',
                    isDraft: false,
                    category: projObj?.category || 'General',
                    subCategory: null,
                    project: projObj ? {
                        _id: projObj._id,
                        name: projObj.name,
                        color: projObj.color,
                        category: projObj.category || 'General',
                    } : null,
                    assignee: resolvedAssignee,
                    startDate: null,
                    dueDate: null,
                    milestoneId: initialMsId,
                    collaborators: [],
                    reviewers: [],
                    subtasks: [],
                    comments: [],
                    attachments: [],
                    dependencies: [],
                };
                setTask(mockTask);
                setItemType(itemType || 'task');
                setMilestoneId(initialMsId);
                setEditTitle('');
                setEditDescription('');
                setPendingAttachments([]);
                setEditPriority('Medium');
                setEditEstHours(0);
                setEditEstMinutes(0);
                setEditEffortHours(0);
                setEditEffortMinutes(0);
                setMilestoneOwner(defaultPm || selectedAssignee || null);
                setCompletionCriteria('');
                setDeliverables([]);
                setInitialChildTasks([]);
                setActualTimeMs(0);
                setSubtasks([]);
                setCollaborators([]);
                setAdditionalProjects([]);
                setExternalRunningTasks({});
                savedSnapshotRef.current = {
                    title: '',
                    description: '',
                    priority: 'Medium',
                    estH: 0,
                    estM: 0,
                    assigneeId: resolvedAssignee ? String(resolvedAssignee._id || resolvedAssignee) : null,
                    projectId: projObj ? String(projObj._id) : null,
                    milestoneId: initialMsId ? String(initialMsId) : null,
                    startDate: null,
                    dueDate: null,
                };
                setTimeout(() => { isInitialMountOrLoadingRef.current = false; }, 350);
                setLoading(false);
                return;
            }

            deletedAttachmentIdsRef.current.clear();
            const { data } = await fetchTaskById(taskId);
            const loadedTask = data.result;
            if (loadedTask.isDraft || loadedTask.status === 'Draft') {
                loadedTask.isDraft = true;
                loadedTask.status = 'Draft';
            }
            setTask(loadedTask);
            setItemType(loadedTask.itemType || 'task');
            if (updateStackItem && loadedTask?.title) {
                updateStackItem(taskId, loadedTask.title, loadedTask.itemType || 'task', loadedTask.parentId);
            }
            const loadedMsId = loadedTask.milestoneId?._id ||
                loadedTask.milestoneId ||
                (loadedTask.parentId?.itemType === 'milestone' ? (loadedTask.parentId._id || loadedTask.parentId) : null) ||
                null;
            setMilestoneId(loadedMsId);
            setMilestoneOwner(loadedTask.milestoneOwner || null);
            setCompletionCriteria(loadedTask.completionCriteria || '');
            setDeliverables(loadedTask.deliverables || []);
            setActualTimeMs(loadedTask.actualTimeMs || 0);

            const childList = (loadedTask.itemType === 'milestone' && Array.isArray(loadedTask.childTasks) && loadedTask.childTasks.length > 0)
                ? loadedTask.childTasks
                : (loadedTask.subtasks || []);

            setSubtasks([...childList].sort((a, b) => (a.order || 0) - (b.order || 0)));
            setCollaborators(loadedTask.collaborators || []);
            setReviewers(loadedTask.reviewers || []);
            setAdditionalProjects(loadedTask.additionalProjects || []);
            setEditTitle(loadedTask.title || '');
            setEditDescription(loadedTask.description || '');
            setEditPriority(loadedTask.priority || 'Medium');
            const est = parseEstimatedTimeString(loadedTask.estimatedTime || '');
            setEditEstHours(est.h);
            setEditEstMinutes(est.m);
            const effort = msToHM(loadedTask.actualTimeMs || 0);
            setEditEffortHours(effort.h);
            setEditEffortMinutes(effort.m);
            // Save loaded values as clean snapshot for dirty detection
            savedSnapshotRef.current = {
                title: loadedTask.title || '',
                description: loadedTask.description || '',
                priority: loadedTask.priority || 'Medium',
                estH: est.h,
                estM: est.m,
                assigneeId: loadedTask.assignee?._id || loadedTask.assignee || null,
                projectId: loadedTask.project?._id || loadedTask.project || null,
                milestoneId: loadedMsId,
                startDate: loadedTask.startDate || null,
                dueDate: loadedTask.dueDate || null,
            };
            setTimeout(() => { isInitialMountOrLoadingRef.current = false; }, 350);

            if (loadedTask.shareToken && loadedTask.shareTokenExpiresAt && new Date(loadedTask.shareTokenExpiresAt) > new Date()) {
                setShareUrl(`${window.location.origin}/shared/task/${loadedTask.shareToken}`);
            } else {
                setShareUrl(null);
            }

            // â”€â”€ Restore live timer state on open â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
            // If another employee was already running this task, the drawer won't
            // receive the socket event that was fired earlier. Fetch attendance state
            // directly so the play/stop button and live ticker are correct immediately.
            // Fire activityLog fetch in background — drawer is already fully rendered above.
            // Once this second request resolves, the activity timeline quietly populates.
            fetchTaskActivityLog(taskId).then(({ data: actData }) => {
                const actLog = actData?.result || [];
                setTask((prev) => (prev && String(prev._id) === String(loadedTask._id))
                    ? { ...prev, activityLog: actLog }
                    : prev
                );
            }).catch(() => { /* Non-critical — activity log remains empty on failure */ });

            try {
                const roleName = currentAdmin?.role?.toLowerCase() || '';
                const isTrackedRole = roleName && !['client', 'guest'].includes(roleName);
                if (isTrackedRole) {
                    const { data: timerData } = await fetchTaskTimerState(taskId);
                    if (timerData?.result) {
                        const { attendanceId, activeTaskEntry } = timerData.result;
                        setExternalRunningTasks({ [attendanceId]: activeTaskEntry });
                    } else {
                        setExternalRunningTasks({});
                    }
                } else {
                    setExternalRunningTasks({});
                }
            } catch {
                // Non-critical â€” timer state will still update via socket
            }
        } catch {
            setLoadError(true);
        } finally {
            inFlightTaskIdRef.current = null;
            setLoading(false);
        }
    }, [taskId, mode, selectedProject, initialMilestoneId, parentId]);

    useEffect(() => {
        if (open) {
            isInitialMountOrLoadingRef.current = true;
            if (taskId && isTop && inFlightTaskIdRef.current !== taskId) loadTask();
            // If workspaceTasks are passed in from parent, use them directly (0 API calls!)
            if (Array.isArray(workspaceTasks) && workspaceTasks.length > 0) {
                setProjectTasks(workspaceTasks);
            } else {
                const loadScopedTasks = async () => {
                    const now = Date.now();
                    if (cachedAllTasks && (now - lastTasksFetch < 5 * 60 * 1000)) {
                        setProjectTasks(cachedAllTasks);
                        return;
                    }
                    try {
                        const targetProjId = task?.project?._id || task?.project || selectedProject?._id || selectedProject;
                        const queryParam = targetProjId ? { projectId: targetProjId, limit: 100 } : { limit: 100 };
                        const { data } = await fetchTasks(queryParam);
                        const fetched = data?.result || [];
                        cachedAllTasks = fetched;
                        lastTasksFetch = now;
                        setProjectTasks(fetched);
                    } catch { /* ignore */ }
                };
                loadScopedTasks();
            }
        }
        if (!open) {
            inFlightTaskIdRef.current = null;
            setTask(null);
            setCommentText('');
            setLoadError(false);
            setCommentAttachments([]);
            setPendingAttachments([]);
            setAttachmentsDragOver(false);
            setActualTimeMs(0);
            setSubtasks([]);
            setCollaborators([]);
            setReviewers([]);
            setAdditionalProjects([]);
            setMilestoneId(null);
            setProjectTasks([]);
            setActiveTab('comments');
            setAddingSubtask(false);
            setNewSubtaskTitle('');
            setShareUrl(null);
            setEditTitle('');
            setEditDescription('');
            setEditPriority('Medium');
            setEditEstHours(0);
            setEditEstMinutes(0);
            setEditEffortHours(0);
            setEditEffortMinutes(0);
            setPreImproveDescription(null);
            setExternalRunningTasks({});
        }
    }, [open, taskId, isTop, loadTask]);


    useEffect(() => {
        if (!open || !taskId || !currentAdminId) return;
        const socket = connectSocket({ userId: currentAdminId, role: currentAdmin?.role });
        if (!socket) return;
        const effortHandler = (payload) => {
            if (String(payload?.taskId) !== String(taskId)) return;
            if (typeof payload.actualTimeMs === 'number') {
                setActualTimeMs(payload.actualTimeMs);
            }
            loadTask();
        };
        const taskUpdatedHandler = (payload) => {
            if (String(payload?.taskId) === String(taskId)) {
                if (payload?.changeType === 'comment_reaction_toggled' && payload?.commentId && payload?.reactions) {
                    setTask((prev) => {
                        if (!prev?.comments) return prev;
                        const updated = prev.comments.map((c) => {
                            if (String(c._id) !== String(payload.commentId)) return c;
                            return { ...c, reactions: payload.reactions };
                        });
                        return { ...prev, comments: updated };
                    });
                    return;
                }
                loadTask();
            }
        };
        const attendanceHandler = (payload) => {
            const attendanceRecord = payload?.attendance;
            const attendanceId = attendanceRecord?._id;
            if (!attendanceId) return;

            const activeTask = attendanceRecord?.dailyTask?.find((dailyTask) => {
                const linkedTaskId = dailyTask?.projectTaskRef?._id || dailyTask?.projectTaskRef;
                return String(linkedTaskId || '') === String(taskId)
                    && String(attendanceRecord?.activeTaskId || '') === String(dailyTask?._id || '');
            });

            setExternalRunningTasks((prev) => {
                const next = { ...prev };
                if (activeTask) next[attendanceId] = activeTask;
                else delete next[attendanceId];
                return next;
            });
        };
        socket.on('projectTask:effortUpdated', effortHandler);
        socket.on('projectTask:updated', taskUpdatedHandler);
        socket.on('attendance:state-updated', attendanceHandler);
        return () => {
            socket.off('projectTask:effortUpdated', effortHandler);
            socket.off('projectTask:updated', taskUpdatedHandler);
            socket.off('attendance:state-updated', attendanceHandler);
            releaseSocket();
        };
    }, [open, taskId, currentAdminId, currentAdmin?.role, loadTask]);

    useEffect(() => {
        const ownRunningTask = attendance?.dailyTask?.find((dailyTask) => {
            const linkedTaskId = dailyTask?.projectTaskRef?._id || dailyTask?.projectTaskRef;
            return String(linkedTaskId || '') === String(task?._id || taskId || '')
                && String(attendance?.activeTaskId || '') === String(dailyTask?._id || '');
        });
        const externalRunningTask = Object.values(externalRunningTasks)[0];
        if (!ownRunningTask && !externalRunningTask) return;
        const timer = setInterval(() => setLiveTick(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [attendance?.activeTaskId, attendance?.dailyTask, externalRunningTasks, task?._id, taskId]);

    // â”€â”€ Attachment upload helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    const validateAndAddFiles = (fileList, setter) => {
        const valid = Array.from(fileList).filter((file) => {
            const result = validateAttachmentFile(file);
            if (!result.valid) { message.warning(`${file.name}: ${result.reason}`, 5); return false; }
            return true;
        });
        if (valid.length === 0) return;
        setter((prev) => [...prev, ...valid.map(createPendingAttachment)]);
    };

    const updatePendingEntry = (setter, id, changes) =>
        setter((prev) => prev.map((f) => f.id === id ? { ...f, ...changes } : f));

    const uploadFileWithRetry = async (file, setter, maxAttempts = 3) => {
        const controller = new AbortController();
        // Register in ref Map immediately — guaranteed available before React state batching
        uploadAbortControllersRef.current.set(file.id, controller);
        let committedServerId = null; // track if backend already saved the file
        try {
            for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                try {
                    updatePendingEntry(setter, file.id, {
                        status: 'uploading',
                        progress: 0,
                        error: '',
                        abortController: controller,
                    });
                    const fd = new FormData();
                    fd.append('file', file.file);
                    const cfg = file.aiConfig || file.file?.aiConfig;
                    if (cfg) {
                        if (cfg.folderCode) fd.append('folderCode', cfg.folderCode);
                        if (cfg.subfolderName) fd.append('subfolderName', cfg.subfolderName);
                        if (cfg.revisionRound) fd.append('revisionRound', cfg.revisionRound);
                        if (cfg.customFileName) fd.append('customFileName', cfg.customFileName);
                        if (cfg.classification) fd.append('classification', cfg.classification);
                        // Send tagged reviewers so backend can dispatch notifications
                        if (Array.isArray(cfg.taggedReviewers) && cfg.taggedReviewers.length > 0) {
                            fd.append('taggedReviewers', JSON.stringify(cfg.taggedReviewers));
                        }
                    }
                    const { data } = await uploadAttachment(taskId, fd, {
                        signal: controller.signal,
                        onUploadProgress: (event) => {
                            if (!event.total) return;
                            const pct = Math.round((event.loaded * 100) / event.total);
                            updatePendingEntry(setter, file.id, { progress: Math.min(pct, 95) });
                        },
                    });
                    committedServerId = data?.result?._id || null;
                    // Check if user cancelled while the response was in-flight (abort after server commit)
                    if (controller.signal.aborted) {
                        // Rollback: silently delete the just-committed file from backend
                        if (committedServerId && taskId) {
                            deleteAttachment(taskId, String(committedServerId)).catch(() => {});
                        }
                        return { aborted: true };
                    }
                    updatePendingEntry(setter, file.id, { status: 'done', progress: 100, error: '', serverId: committedServerId });
                    return data?.result || null;
                } catch (err) {
                    if (err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
                        // If backend somehow committed before signal propagated, rollback
                        if (committedServerId && taskId) {
                            deleteAttachment(taskId, String(committedServerId)).catch(() => {});
                        }
                        return { aborted: true };
                    }
                    if (attempt === maxAttempts) {
                        updatePendingEntry(setter, file.id, { status: 'error', error: err?.response?.data?.message || 'Upload failed.' });
                        return null;
                    }
                    await new Promise((r) => setTimeout(r, 600 * attempt));
                }
            }
        } finally {
            uploadAbortControllersRef.current.delete(file.id);
        }
        return null;
    };

    // â”€â”€ Attachments tab handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    const handleAttachmentTabFiles = (fileList) => validateAndAddFiles(fileList, setPendingAttachments);

    const handleAttachmentTabDrop = (e) => {
        e.preventDefault();
        setAttachmentsDragOver(false);
        handleAttachmentTabFiles(e.dataTransfer.files);
    };

    const handleUploadPendingAttachments = async () => {
        const toUpload = pendingAttachments.filter((f) => f.status === 'pending' || f.status === 'error');
        if (toUpload.length === 0) return;
        // Snapshot of uploaded IDs before any removes can fire
        const uploadedIdSet = new Set(toUpload.map((f) => f.id));
        const results = await Promise.all(toUpload.map((f) => uploadFileWithRetry(f, setPendingAttachments)));
        // Only count truly succeeded (non-null, non-aborted) results whose entry still exists in state
        const succeeded = results.filter((r) => r && !r.aborted);
        // Further filter: skip any file the user removed while upload was in-flight
        const currentIds = new Set(
            // Read via functional updater trick is not possible here; use the ref snapshot above
            // The aborted ones already rollback server-side, so we just exclude them from the UI add
            toUpload.map((f) => f.id).filter((id) => !uploadAbortControllersRef.current.has(id))
        );
        const committedToShow = succeeded.filter((_, i) => currentIds.has(toUpload[i]?.id));
        if (committedToShow.length > 0) {
            // Refresh task attachments list
            setTask((prev) => ({ ...prev, attachments: [...(prev?.attachments || []), ...committedToShow] }));
            // Clear uploaded entries from pending list
            setPendingAttachments((prev) => prev.filter((f) => !uploadedIdSet.has(f.id) || f.status !== 'done'));
            message.success(`${committedToShow.length} file${committedToShow.length > 1 ? 's' : ''} uploaded.`);

            // Record attachment upload event in comments timeline (Asana style)
            const fileNames = committedToShow.map((f) => f.originalName || f.fileName || 'file').join(', ');
            addComment(taskId, {
                text: `📎 Attached: ${fileNames}`,
                attachments: committedToShow,
                isSystemGenerated: true,
                source: 'attachment_upload',
            }).then(({ data }) => {
                if (data?.result) {
                    setTask((prev) => ({
                        ...prev,
                        comments: [...(prev?.comments || []), data.result],
                    }));
                }
            }).catch((err) => console.warn('Attachment timeline comment error:', err));
        }
        // Only show error toast for actual failures, not for user-cancelled uploads
        const failedCount = results.filter((r) => r === null).length;
        if (failedCount > 0) message.error(`${failedCount} file${failedCount > 1 ? 's' : ''} failed to upload.`);
    };

    const removePendingAttachment = (id) => {
        // Abort via ref Map first � guaranteed, no React state-batch delay
        const ctrl = uploadAbortControllersRef.current.get(id);
        if (ctrl) { try { ctrl.abort(); } catch { /* ignore */ } }
        setPendingAttachments((prev) => {
            const removed = prev.find((f) => f.id === id);
            cleanupPendingAttachment(removed);
            return prev.filter((f) => f.id !== id);
        });
    };

    // â”€â”€ Comment attachment handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    const handleCommentFiles = (fileList) => validateAndAddFiles(fileList, setCommentAttachments);

    const removeCommentAttachment = (id) => {
        // Abort via ref Map first � guaranteed, no React state-batch delay
        const ctrl = uploadAbortControllersRef.current.get(id);
        if (ctrl) { try { ctrl.abort(); } catch { /* ignore */ } }
        setCommentAttachments((prev) => {
            const removed = prev.find((f) => f.id === id);
            cleanupPendingAttachment(removed);
            return prev.filter((f) => f.id !== id);
        });
    };

    const handleAddComment = async () => {
        const textEmpty = isQuillEmpty(commentText);
        if (textEmpty && commentAttachments.length === 0 && (commentFileReferences?.length || 0) === 0) return;
        setSubmittingComment(true);
        try {
            // Upload any comment attachments first, then post the comment
            let succeededAttachments = [];
            let updatedCommentText = commentText || '';
            if (commentAttachments.length > 0) {
                const results = await Promise.all(
                    commentAttachments.map((f) => uploadFileWithRetry(f, setCommentAttachments))
                );
                succeededAttachments = results.filter(Boolean);
                await Promise.all(succeededAttachments.map(async (r, idx) => {
                    const originalEntry = commentAttachments[idx];
                    const blobUrl = originalEntry?.previewUrl;
                    let url = r.downloadUrl || r.publicUrl;
                    if (!url && r._id && taskId) {
                        try {
                            const { data } = await getDownloadUrl(taskId, r._id, 'inline');
                            url = data?.result?.downloadUrl || data?.result?.publicUrl;
                            if (url) r.downloadUrl = url;
                        } catch (e) {
                            // ignore
                        }
                    }
                    if (url) {
                        setOptimisticImageUrl(r._id, url);
                        if (r.originalName) setOptimisticImageUrl(r.originalName, url);
                        if (r.fileName) setOptimisticImageUrl(r.fileName, url);
                        if (blobUrl && updatedCommentText) {
                            updatedCommentText = updatedCommentText.replaceAll(blobUrl, url);
                        }
                    }
                }));
                if (succeededAttachments.length > 0) {
                    setTask((prev) => ({ ...prev, attachments: [...(prev?.attachments || []), ...succeededAttachments] }));
                }
            }

            // Build comment text — only append attachment names if no text was typed, or for non-image files
            const nonImageUploadedNames = succeededAttachments
                .filter((att) => !isImageFile(att))
                .map((r) => r.originalName || r.fileName || '')
                .filter(Boolean);
            const attachmentNote = nonImageUploadedNames.length > 0
                ? `\n📎 Attached: ${nonImageUploadedNames.join(', ')}`
                : '';
            const rawBody = textEmpty ? '' : (updatedCommentText.trim() === '<p><br></p>' ? '' : updatedCommentText.trim());
            const finalText = (rawBody + (rawBody ? '' : attachmentNote)).trim() || attachmentNote.trim();

            if (finalText || succeededAttachments.length > 0) {
                const fileRefIds = (commentFileReferences || []).map((f) => f._id || f.id);
                const { data } = await addComment(taskId, {
                    text: finalText || 'Added attachments',
                    attachments: succeededAttachments,
                    fileReferences: fileRefIds,
                    mentions: commentMentions,
                });
                if (data?.result) {
                    setTask((prev) => ({ ...prev, comments: [...(prev?.comments || []), data.result] }));
                }
            }
            setCommentText('');
            setCommentAttachments([]);
            setCommentFileReferences([]);
            setCommentMentions([]);
        } catch {
            message.error('Failed to add comment.');
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            await deleteComment(taskId, commentId);
            const targetComment = task?.comments?.find((c) => (c._id || c) === commentId);
            if (targetComment?.attachments) {
                targetComment.attachments.forEach((att) => {
                    if (att._id) deleteOptimisticImageUrl(att._id);
                    if (att.originalName) deleteOptimisticImageUrl(att.originalName);
                    if (att.fileName) deleteOptimisticImageUrl(att.fileName);
                });
            }
            setTask((prev) => ({ ...prev, comments: prev.comments.filter((c) => c._id !== commentId) }));
        } catch {
            message.error('Failed to delete comment.');
        }
    };

    const handleToggleCommentReaction = async (commentId, emoji) => {
        if (!commentId || !emoji || !taskId) return;
        const prevComments = task?.comments || [];
        const currentAdminUser = employees?.find((e) => String(e._id) === String(currentAdminId)) || currentAdmin;
        const currentUserName = getFullName(currentAdminUser) || 'You';

        // Optimistic UI update
        setTask((prev) => {
            if (!prev?.comments) return prev;
            const updated = prev.comments.map((c) => {
                if (String(c._id) !== String(commentId)) return c;
                const currentReactions = Array.isArray(c.reactions) ? [...c.reactions] : [];
                const existingIndex = currentReactions.findIndex(
                    (r) => r.emoji === emoji && String(r.user?._id || r.user || r.userId) === String(currentAdminId)
                );
                let nextReactions;
                if (existingIndex >= 0) {
                    nextReactions = currentReactions.filter((_, idx) => idx !== existingIndex);
                } else {
                    nextReactions = [
                        ...currentReactions,
                        { emoji, user: currentAdminId, userName: currentUserName, createdAt: new Date().toISOString() },
                    ];
                }
                return { ...c, reactions: nextReactions };
            });
            return { ...prev, comments: updated };
        });

        try {
            const { data } = await toggleCommentReaction(taskId, commentId, emoji);
            if (data?.result?.reactions) {
                setTask((prev) => {
                    if (!prev?.comments) return prev;
                    const updated = prev.comments.map((c) => {
                        if (String(c._id) !== String(commentId)) return c;
                        return { ...c, reactions: data.result.reactions };
                    });
                    return { ...prev, comments: updated };
                });
            }
        } catch (err) {
            setTask((prev) => ({ ...prev, comments: prevComments }));
            message.error(err?.response?.data?.message || 'Failed to update reaction.');
        }
    };

    const stripAttachmentFromDescriptionHtml = (html, attachment) => {
        if (!html || typeof html !== 'string' || !attachment) return html;
        let updated = html;
        const attId = String(attachment._id || '');
        const attName = (attachment.originalName || attachment.fileName || '').trim();

        if (attId) {
            const escapedId = attId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            updated = updated.replace(new RegExp(`<img[^>]*${escapedId}[^>]*>`, 'gi'), '');
            updated = updated.replace(new RegExp(`<a[^>]*${escapedId}[^>]*>.*?<\\/a>`, 'gi'), '');
        }
        if (attName) {
            const escapedName = attName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const encodedName = encodeURIComponent(attName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            updated = updated.replace(new RegExp(`\\s*@File\\[${escapedName}\\]\\s*`, 'gi'), ' ');
            updated = updated.replace(new RegExp(`<a[^>]*>[^<]*${escapedName}[^<]*<\\/a>`, 'gi'), '');
            updated = updated.replace(new RegExp(`<img[^>]*${encodedName}[^>]*>`, 'gi'), '');
        }
        return updated;
    };

    const clearAttachmentUrlCaches = (att, fallbackId = null) => {
        if (fallbackId) deleteOptimisticImageUrl(String(fallbackId));
        if (!att) return;
        if (att._id) deleteOptimisticImageUrl(String(att._id));
        if (att.originalName) deleteOptimisticImageUrl(att.originalName);
        if (att.fileName) deleteOptimisticImageUrl(att.fileName);
        if (att.previewUrl) deleteOptimisticImageUrl(att.previewUrl);
        if (att.publicUrl) deleteOptimisticImageUrl(att.publicUrl);
        if (att.downloadUrl) deleteOptimisticImageUrl(att.downloadUrl);
    };

    const handleDeleteAttachment = async (attachmentId, customCleanedDescription = null) => {
        const strId = String(attachmentId);
        // Cancel any pending debounced description auto-save so it cannot overwrite the deletion
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
            setIsAutoSaving(false);
        }
        deletedAttachmentIdsRef.current.add(strId);
        cachedAllTasks = null;

        const prevAttachments = task?.attachments || [];
        const prevDescription = editDescription;
        const deletedAtt = prevAttachments.find((a) => String(a._id) === strId);
        clearAttachmentUrlCaches(deletedAtt, strId);

        let cleanedDesc = customCleanedDescription !== null ? customCleanedDescription : editDescription;
        if (deletedAtt && cleanedDesc) {
            cleanedDesc = stripAttachmentFromDescriptionHtml(cleanedDesc, deletedAtt);
        }

        const nextAttachments = prevAttachments.filter((a) => String(a._id) !== strId);
        setTask((prev) => (prev ? { ...prev, attachments: nextAttachments, description: cleanedDesc ?? prev.description } : prev));
        if (cleanedDesc !== undefined && cleanedDesc !== editDescription) {
            setEditDescription(cleanedDesc);
        }
        if (savedSnapshotRef.current && cleanedDesc !== undefined) {
            savedSnapshotRef.current.description = cleanedDesc;
        }

        try {
            const { data } = await deleteAttachment(taskId, strId);
            const serverDesc = customCleanedDescription !== null
                ? cleanedDesc
                : (data?.description !== undefined ? data.description : cleanedDesc);
            if (savedSnapshotRef.current && serverDesc !== undefined) {
                savedSnapshotRef.current.description = serverDesc;
            }
            if (customCleanedDescription !== null && serverDesc !== data?.description && taskId && taskId !== 'new') {
                await updateTask(taskId, { description: serverDesc }).catch(() => {});
            }
            if (task) {
                onTaskUpdated?.({ ...task, attachments: nextAttachments, description: serverDesc ?? task.description });
            }
            message.success('Attachment removed.');
        } catch {
            deletedAttachmentIdsRef.current.delete(strId);
            setTask((prev) => (prev ? { ...prev, attachments: prevAttachments, description: prevDescription } : prev));
            setEditDescription(prevDescription);
            message.error('Failed to remove attachment.');
        }
    };

    const handleBulkDeleteAttachments = async (attachmentIds, customCleanedDescription = null) => {
        if (!attachmentIds || attachmentIds.length === 0) return [];
        const toDeleteIds = attachmentIds.map(String);

        // Cancel any pending debounced description auto-save so it cannot overwrite the deletion
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
            setIsAutoSaving(false);
        }
        toDeleteIds.forEach((id) => deletedAttachmentIdsRef.current.add(id));
        cachedAllTasks = null;

        // 1. Instant Optimistic UI Update: files disappear immediately
        const prevAttachments = task?.attachments || [];
        const prevDescription = editDescription;
        const deletedAtts = prevAttachments.filter((a) => toDeleteIds.includes(String(a._id)));

        toDeleteIds.forEach((id) => deleteOptimisticImageUrl(id));
        deletedAtts.forEach((att) => clearAttachmentUrlCaches(att));

        let updatedDesc = customCleanedDescription !== null ? customCleanedDescription : editDescription;
        if (updatedDesc && deletedAtts.length > 0) {
            deletedAtts.forEach((att) => {
                updatedDesc = stripAttachmentFromDescriptionHtml(updatedDesc, att);
            });
        }

        const nextAttachments = prevAttachments.filter((a) => !toDeleteIds.includes(String(a._id)));
        setTask((prev) => ({
            ...prev,
            attachments: nextAttachments,
            description: updatedDesc ?? prev?.description,
        }));

        if (updatedDesc !== undefined && updatedDesc !== editDescription) {
            setEditDescription(updatedDesc);
        }
        if (savedSnapshotRef.current && updatedDesc !== undefined) {
            savedSnapshotRef.current.description = updatedDesc;
        }

        // 2. Fast single-endpoint backend call (atomic MongoDB save, preserves Cloudflare R2)
        try {
            const { data } = await bulkDeleteAttachments(taskId, toDeleteIds);
            const serverDeleted = (data?.deletedIds || toDeleteIds).map(String);
            const skipped = (data?.skippedIds || []).map(String);

            if (customCleanedDescription !== null && updatedDesc !== data?.description && taskId && taskId !== 'new') {
                await updateTask(taskId, { description: updatedDesc }).catch(() => {});
            }

            if (skipped.length > 0) {
                skipped.forEach((id) => deletedAttachmentIdsRef.current.delete(id));
                const restoreAtts = prevAttachments.filter((a) => skipped.includes(String(a._id)));
                if (restoreAtts.length > 0) {
                    setTask((prev) => ({
                        ...prev,
                        attachments: [...(prev?.attachments || []), ...restoreAtts],
                    }));
                }
                if (serverDeleted.length > 0) {
                    message.success(`Successfully removed ${serverDeleted.length} attachment${serverDeleted.length > 1 ? 's' : ''}.`);
                }
            } else {
                if (task) {
                    onTaskUpdated?.({ ...task, attachments: nextAttachments, description: updatedDesc ?? task.description });
                }
                message.success(`Successfully removed ${serverDeleted.length} attachment${serverDeleted.length > 1 ? 's' : ''}.`);
            }
            return serverDeleted;
        } catch (err) {
            toDeleteIds.forEach((id) => deletedAttachmentIdsRef.current.delete(id));
            setTask((prev) => ({ ...prev, attachments: prevAttachments, description: prevDescription }));
            setEditDescription(prevDescription);
            message.error(err?.response?.data?.message || 'Failed to remove selected attachments.');
            return [];
        }
    };

    const handleToggleSubtask = async (sub) => {
        // For real tasks, completed = status is 'Completed'
        const isNowCompleted = sub.status !== 'Completed';
        setSubtasks((prev) => prev.map((s) => s._id === sub._id ? { ...s, status: isNowCompleted ? 'Completed' : 'Backlog' } : s));
        try {
            await updateSubtask(taskId, sub._id, { completed: isNowCompleted });
        } catch {
            setSubtasks((prev) => prev.map((s) => s._id === sub._id ? { ...s, status: sub.status } : s));
            message.error('Failed to update subtask.');
        }
    };

    // Opens the subtask in the Task Drawer stack (pushes onto the navigation stack)
    const handleOpenSubtask = (sub) => {
        if (pushTask) {
            pushTask(sub._id, 'view', sub.title || 'Subtask');
        }
    };

    const handleAddSubtask = async () => {
        if (!newSubtaskTitle.trim()) return;
        setSavingSubtask(true);
        try {
            const { data } = await addSubtask(taskId, newSubtaskTitle.trim());
            if (data?.result) setSubtasks((prev) => [...prev, data.result]);
            setNewSubtaskTitle('');
            setAddingSubtask(false);
        } catch {
            message.error('Failed to add subtask.');
        } finally {
            setSavingSubtask(false);
        }
    };

    const handleDeleteSubtask = async (subId) => {
        const previous = [...subtasks];
        setSubtasks((items) => items.filter((item) => item._id !== subId));
        try {
            await deleteSubtask(taskId, subId);
        } catch {
            setSubtasks(previous);
            message.error('Failed to delete subtask.');
        }
    };

    const handleSubtaskTitleEdit = async (sub, newTitle) => {
        if (!newTitle.trim() || newTitle.trim() === sub.title) return;
        const title = newTitle.trim();
        setSubtasks((prev) => prev.map((s) => s._id === sub._id ? { ...s, title } : s));
        try {
            await updateSubtask(taskId, sub._id, { title });
        } catch {
            setSubtasks((prev) => prev.map((s) => s._id === sub._id ? { ...s, title: sub.title } : s));
            message.error('Failed to update subtask.');
        }
    };

    const handleSubtaskDragEnd = async (result) => {
        // Drag-to-reorder is a UI-only operation for now — subtasks are real tasks
        // and ordering is preserved client-side without a backend call
        if (!result.destination || result.destination.index === result.source.index) return;
        const reordered = Array.from(subtasks);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);
        setSubtasks(reordered);
    };


    const handlePrimaryProjectChange = async (projectId) => {
        const isDraftOrCreate = mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft';
        if (!projectId && !isDraftOrCreate) {
            message.warning('A task must belong to a project.');
            return;
        }
        const proj = projectId ? projects.find(p => String(p._id) === String(projectId)) : null;
        const optimistic = proj ? { _id: proj._id, name: proj.name, color: proj.color, category: proj.category || 'General' } : null;
        const previous = task?.project;
        const previousMsId = milestoneId;
        setTask(prev => ({ ...prev, project: optimistic, milestoneId: null, category: prev?.category || optimistic?.category || 'General' }));
        setMilestoneId(null);
        // In create or draft mode, just update local state — no backend call yet
        if (isDraftOrCreate) return;
        // For existing tasks, persist the change immediately
        setIsAutoSaving(true);
        try {
            const { data } = await updateTask(taskId, { project: projectId || null, milestoneId: null });
            setTask(prev => ({ ...prev, project: data?.result?.project || optimistic, milestoneId: null }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask(prev => ({ ...prev, project: previous, milestoneId: previousMsId }));
            setMilestoneId(previousMsId);
            message.error('Failed to update project.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const handleMilestoneChange = async (newMilestoneId) => {
        const msId = newMilestoneId || null;
        const previousMsId = milestoneId;
        setMilestoneId(msId);
        setTask(prev => prev ? ({ ...prev, milestoneId: msId }) : prev);
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const { data } = await updateTask(taskId, { milestoneId: msId });
            const savedMsId = data?.result?.milestoneId?._id || data?.result?.milestoneId || msId;
            setMilestoneId(savedMsId);
            setTask(prev => prev ? ({ ...prev, milestoneId: data?.result?.milestoneId || msId }) : prev);
            onTaskUpdated?.(data?.result);
        } catch {
            setMilestoneId(previousMsId);
            setTask(prev => prev ? ({ ...prev, milestoneId: previousMsId }) : prev);
            message.error('Failed to update milestone.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const uploadPendingAttachmentsForTask = async (targetTaskId, baseDescription = '') => {
        const uploadTargets = pendingAttachments.filter((file) => file.status !== 'done');
        if (uploadTargets.length === 0 || !targetTaskId) {
            return { updatedDescription: baseDescription, uploadedAtts: [], descriptionChanged: false };
        }

        let updatedDescription = baseDescription;
        let descriptionChanged = false;
        const uploadedAtts = [];

        for (const entry of uploadTargets) {
            const fd = new FormData();
            fd.append('file', entry.file);
            try {
                const { data: upData } = await uploadAttachment(targetTaskId, fd);
                const newAtt = upData?.result;
                if (newAtt) {
                    uploadedAtts.push(newAtt);
                    const previewUrl = (newAtt._id ? getAttachmentPreviewUrl(targetTaskId, newAtt._id) : null) || newAtt.previewUrl || newAtt.publicUrl || newAtt.downloadUrl;
                    const blobUrl = entry.tempBlobUrl || entry.previewUrl;
                    if (previewUrl) {
                        if (newAtt._id) setOptimisticImageUrl(newAtt._id, previewUrl);
                        if (newAtt.originalName) setOptimisticImageUrl(newAtt.originalName, previewUrl);
                        if (blobUrl) {
                            setOptimisticImageUrl(blobUrl, previewUrl);
                            if (updatedDescription.includes(blobUrl)) {
                                updatedDescription = updatedDescription.replaceAll(blobUrl, previewUrl);
                                descriptionChanged = true;
                            }
                        }
                    }
                }
            } catch (e) {
                console.error('Failed to upload attachment', e);
            }
        }

        if (descriptionChanged) {
            try {
                await updateTask(targetTaskId, { description: updatedDescription });
            } catch (e) {
                console.error('Failed to update task description with permanent attachment URLs:', e);
            }
        }

        setPendingAttachments([]);
        return { updatedDescription, uploadedAtts, descriptionChanged };
    };

    const handleCreateTask = async () => {
        const isMilestone = (task?.itemType === 'milestone') || (itemType === 'milestone');
        const itemLabel = isMilestone ? 'Milestone' : 'Task';
        const isExistingDraft = Boolean(task?._id && task._id !== 'new' && (task.isDraft || task.status === 'Draft'));

        if (!editTitle.trim()) {
            message.warning(`${itemLabel} title is required.`);
            return;
        }
        if (!task?.project?._id && !task?.project) {
            message.warning('Please select a project.');
            return;
        }

        const resolvedStatus = (!task?.status || task.status === 'Draft')
            ? 'Backlog'
            : task.status;

        // ── Required field validation for tasks (not milestones) ──
        if (!isMilestone) {
            if (!editTitle.trim()) {
                message.warning('Task title is required.');
                return;
            }
            if (!editDescription.trim()) {
                message.warning('Task description is required. Please provide a detailed description.');
                return;
            }
            if (!task?.assignee?._id && !task?.assignee) {
                message.warning('Assignee is required. Please assign this task to a team member.');
                return;
            }
            if (!editPriority) {
                message.warning('Priority is required. Please select a priority level.');
                return;
            }
            if (!resolvedStatus) {
                message.warning('Status is required. Please select a status.');
                return;
            }
            if (!task?.startDate) {
                message.warning('Start date is required. Please set a start date.');
                return;
            }
            if (!task?.dueDate) {
                message.warning('Due date is required. Please set a due date.');
                return;
            }
            if (!milestoneId && !task?.milestoneId) {
                message.warning('Milestone is required. Please assign this task to a milestone.');
                return;
            }
            if (!editEstHours && !editEstMinutes) {
                message.warning('Estimated time is required. Please enter the estimated hours and/or minutes.');
                return;
            }
        }

        setUpdatingTask(true);
        try {
            const estimatedTime = buildEstimatedTimeString(editEstHours, editEstMinutes);
            const projectIdVal = task.project?._id || task.project;
            const assigneeIdVal = task.assignee?._id || task.assignee;
            const payload = {
                title: editTitle.trim(),
                description: editDescription?.trim(),
                project: projectIdVal,
                itemType: isMilestone ? 'milestone' : 'task',
                milestoneOwner: milestoneOwner?._id || milestoneOwner || undefined,
                completionCriteria: completionCriteria?.trim() || '',
                deliverables: deliverables || [],
                assignee: isMilestone ? undefined : assigneeIdVal,
                collaborators: isMilestone ? [] : collaborators.map(c => c._id || c),
                reviewers: isMilestone ? [] : reviewers.map(r => r._id || r),
                additionalProjects: additionalProjects.map(p => p._id || p),
                priority: editPriority,
                status: resolvedStatus,
                isDraft: false,
                startDate: task.startDate,
                dueDate: task.dueDate,
                estimatedTime: isMilestone ? undefined : estimatedTime,
                parentId: (parentId && parentId !== 'new') ? parentId : undefined,
                milestoneId: milestoneId || initialMilestoneId || ((parentId && parentId !== 'new') ? parentId : undefined),
                billableStatus: task?.billableStatus || 'Billable',
                billingClearance: task?.billingClearance || 'Unbilled',
                category: task?.category || undefined,
                subCategory: task?.subCategory || undefined,
            };

            let createdTask;
            if (isExistingDraft) {
                const { data } = await updateTask(task._id, payload);
                createdTask = data?.result || { ...task, ...payload, isDraft: false, status: resolvedStatus };
            } else {
                const { data } = await createTask(payload);
                createdTask = data?.result;
            }

            // If creating a milestone and initialChildTasks were specified, create them now!
            if (isMilestone && initialChildTasks.length > 0 && createdTask?._id) {
                for (const childTitle of initialChildTasks) {
                    try {
                        await createTask({
                            title: childTitle,
                            project: createdTask.project?._id || createdTask.project,
                            milestoneId: createdTask._id,
                            status: 'Backlog',
                            priority: editPriority || 'Medium',
                        });
                    } catch (e) {
                        console.error('Failed to create initial child task:', e);
                    }
                }
            }

            // Handle pending attachments (including inline images & files added in the description editor)
            if (createdTask?._id) {
                const { updatedDescription, uploadedAtts, descriptionChanged } = await uploadPendingAttachmentsForTask(
                    createdTask._id,
                    editDescription?.trim() || createdTask.description || ''
                );
                if (descriptionChanged) {
                    createdTask.description = updatedDescription;
                    setEditDescription(updatedDescription);
                }
                if (uploadedAtts.length > 0) {
                    createdTask.attachments = [...(createdTask.attachments || []), ...uploadedAtts];
                }
            }

            message.success(`${itemLabel} created successfully!`);
            clearTaskDraft(createdTask?.project?._id || createdTask?.project);

            if (isExistingDraft) {
                setTask((prev) => ({ ...prev, ...createdTask, isDraft: false, status: resolvedStatus }));
                onTaskUpdated?.(createdTask);
            } else {
                onSuccess?.(createdTask);
                // Replace the 'new' stack item with the actual task ID
                if (popTask && pushTask) {
                    popTask();
                    pushTask(createdTask._id, 'view', createdTask.title);
                }
            }
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to create task.');
        } finally {
            setUpdatingTask(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (!task || newStatus === task.status) return;
        const previousStatus = task.status;
        setTask((current) => ({ ...current, status: newStatus }));
        if (mode === 'create' || task?.isDraft || task?.status === 'Draft') return;
        setStatusUpdating(true);
        try {
            await updateTaskStatus(taskId, newStatus);
            onTaskUpdated?.({ ...task, status: newStatus });
        } catch {
            setTask((current) => ({ ...current, status: previousStatus }));
            message.error('Failed to update status.');
        } finally {
            setStatusUpdating(false);
        }
    };

    const handleCompleteTask = () => handleStatusChange('Completed');

    const handleShare = async () => {
        const idToShare = task?._id || taskId;
        if (!idToShare) return;
        setSharingLoading(true);
        try {
            const { data } = await generateShareLink(idToShare);
            if (data?.result?.token) {
                const url = `${window.location.origin}/shared/task/${data.result.token}`;
                setShareUrl(url);
                message.success('Public guest share link generated!');
                return url;
            }
        } catch (err) {
            console.error('[TaskDrawer] Share error:', err);
            message.error(err?.response?.data?.message || 'Failed to generate share link');
        } finally {
            setSharingLoading(false);
        }
    };

    const handleRevokeShare = async () => {
        const idToShare = task?._id || taskId;
        if (!idToShare) return;
        setSharingLoading(true);
        try {
            await revokeShareLink(idToShare);
            setShareUrl(null);
            message.success('Public guest link revoked.');
        } catch (err) {
            console.error('[TaskDrawer] Revoke share error:', err);
            message.error(err?.response?.data?.message || 'Failed to revoke share link');
        } finally {
            setSharingLoading(false);
        }
    };

    const copyInternalLink = () => {
        const identifier = task?._id || taskId;  // Use hex _id for reliable deep-linking
        if (!identifier) return;
        const internalUrl = `${window.location.origin}/project-management?task=${identifier}`;
        navigator.clipboard?.writeText?.(internalUrl);
        message.success('Internal team link copied to clipboard!');
        return internalUrl;
    };

    const getTargetUserId = () => {
        return String(currentAdminId);
    };

    const getProjectTaskAttendanceInfo = () => ({
        taskGid: null,
        parent: null,
        customField: null,
        customFieldTwo: null,
        estimatedField: null,
        AllotedHours: 0,
        totalEffortTime: 0,
        utilizedField: null,
        extraHourTask: false,
        sectionGid: null,
        sectionName: null,
        estimatedTime: task?.estimatedTimeMs || task?.estimatedTime || 0,
        projectTaskRef: task?._id || taskId,
        taskSource: 'pm_dashboard',
        targetUserId: getTargetUserId(),
    });

    const handleStartTimer = async () => {
        if (!task) return;

        if (attendanceStatus !== 'in' || !attendance) {
            message.warning('Please mark time in before starting task tracking.');
            return;
        }
        if (attendance?.isBreaking) {
            message.warning('End your break before starting task tracking.');
            return;
        }
        if (attendance?.activeTaskId) {
            message.warning('A task is already running. Stop it before starting this one.');
            return;
        }

        const payload = getProjectTaskAttendanceInfo();
        const result = await startTask?.(task.title || task.description || 'Project task', payload);
        if (result?.success) {
            await refreshSession?.();
            message.success('Task timer started.');
        }
    };

    const handleStopTimer = async (progressUpdate = null) => {
        const targetUserId = String(currentAdminId);
        const activeTask = attendance?.dailyTask?.find((dailyTask) => String(dailyTask?._id) === String(attendance?.activeTaskId));

        if (!activeTask) return;

        const enrichedTask = {
            ...activeTask,
            isActive: true,
            taskBreakDuration: 0,
            endTime: new Date().toISOString(),
            targetUserId: targetUserId
        };

        const result = await stopTask?.(enrichedTask, progressUpdate);
        if (result?.success) {
            await refreshSession?.();
            message.success('Task timer stopped.');
        }
    };

    const handleAddCollaborator = async (employeeId) => {
        if (!employeeId) return;
        if (collaborators.some((collaborator) => (collaborator._id || collaborator) === employeeId)) return;
        if ((task?.assignee?._id || task?.assignee) === employeeId) {
            message.warning('This person is already the assignee.');
            return;
        }
        const previous = [...collaborators];
        const employee = employees.find((item) => item._id === employeeId);
        const optimistic = employee ? { _id: employee._id, name: employee.name, surname: employee.surname, photo: employee.photo } : { _id: employeeId };
        setCollaborators((prev) => [...prev, optimistic]);
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const newIds = [...collaborators.map((collaborator) => collaborator._id || collaborator), employeeId];
            const { data } = await updateTask(taskId, { collaborators: newIds });
            setCollaborators(data?.result?.collaborators || [...collaborators, optimistic]);
            onTaskUpdated?.(data?.result);
        } catch {
            setCollaborators(previous);
            message.error('Failed to add collaborator.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const handleRemoveCollaborator = async (collabId) => {
        const previous = [...collaborators];
        setCollaborators((prev) => prev.filter((collaborator) => (collaborator._id || collaborator) !== collabId));
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const newIds = collaborators.filter((collaborator) => (collaborator._id || collaborator) !== collabId).map((collaborator) => collaborator._id || collaborator);
            const { data } = await updateTask(taskId, { collaborators: newIds });
            setCollaborators(data?.result?.collaborators || collaborators.filter((collaborator) => (collaborator._id || collaborator) !== collabId));
            onTaskUpdated?.(data?.result);
        } catch {
            setCollaborators(previous);
            message.error('Failed to remove collaborator.');
        } finally {
            setIsAutoSaving(false);
        }
    };


    const handleAddReviewer = async (employeeId) => {
        if (!employeeId) return;
        if (reviewers.some((reviewer) => (reviewer._id || reviewer) === employeeId)) return;
        if ((task?.assignee?._id || task?.assignee) === employeeId) {
            message.warning('This person is already the assignee.');
            return;
        }
        const previous = [...reviewers];
        const employee = employees.find((item) => item._id === employeeId);
        const optimistic = employee ? { _id: employee._id, name: employee.name, surname: employee.surname, photo: employee.photo } : { _id: employeeId };
        setReviewers((prev) => [...prev, optimistic]);
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const newIds = [...reviewers.map((reviewer) => reviewer._id || reviewer), employeeId];
            const { data } = await updateTask(taskId, { reviewers: newIds });
            setReviewers(data?.result?.reviewers || [...reviewers, optimistic]);
            onTaskUpdated?.(data?.result);
        } catch {
            setReviewers(previous);
            message.error('Failed to add reviewer.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const handleRemoveReviewer = async (reviewerId) => {
        const previous = [...reviewers];
        setReviewers((prev) => prev.filter((reviewer) => (reviewer._id || reviewer) !== reviewerId));
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const newIds = reviewers
                .filter((reviewer) => (reviewer._id || reviewer) !== reviewerId)
                .map((reviewer) => reviewer._id || reviewer);
            const { data } = await updateTask(taskId, { reviewers: newIds });
            setReviewers(data?.result?.reviewers || previous.filter((reviewer) => (reviewer._id || reviewer) !== reviewerId));
            onTaskUpdated?.(data?.result);
        } catch {
            setReviewers(previous);
            message.error('Failed to remove reviewer.');
        } finally {
            setIsAutoSaving(false);
        }
    };
    const handleAddProject = async (projectId) => {
        if (!projectId) return;
        if (String(projectId) === String(task?.project?._id || task?.project)) {
            message.warning('This is already the primary project.');
            return;
        }
        if (additionalProjects.some((project) => String(project._id || project) === String(projectId))) return;
        const previous = [...additionalProjects];
        const project = projects.find((item) => String(item._id) === String(projectId));
        const optimistic = project ? { _id: project._id, name: project.name, color: project.color } : { _id: projectId };
        setAdditionalProjects((prev) => [...prev, optimistic]);
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const newIds = [...additionalProjects.map((item) => item._id || item), projectId];
            const { data } = await updateTask(taskId, { additionalProjects: newIds });
            setAdditionalProjects(data?.result?.additionalProjects || [...additionalProjects, optimistic]);
            onTaskUpdated?.(data?.result);
        } catch {
            setAdditionalProjects(previous);
            message.error('Failed to add project association.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const handleRemoveAdditionalProject = async (projId) => {
        const previous = [...additionalProjects];
        setAdditionalProjects((prev) => prev.filter((project) => String(project._id || project) !== String(projId)));
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const newIds = additionalProjects.filter((project) => String(project._id || project) !== String(projId)).map((project) => project._id || project);
            const { data } = await updateTask(taskId, { additionalProjects: newIds });
            setAdditionalProjects(data?.result?.additionalProjects || previous.filter((project) => String(project._id || project) !== String(projId)));
            onTaskUpdated?.(data?.result);
        } catch {
            setAdditionalProjects(previous);
            message.error('Failed to remove project association.');
        } finally {
            setIsAutoSaving(false);
        }
    };


    const handleImproveDescription = async () => {
        if (!editDescription.trim()) {
            message.warning('Please enter a description before using Improve with AI.');
            return;
        }
        setPreImproveDescription(editDescription); // save snapshot for undo
        setImprovingDescription(true);
        try {
            const { data } = await improveTaskDescription(taskId || 'new', editDescription);
            if (data?.result) {
                setEditDescription(data.result);
                message.success('Description improved! Review and save when ready.');
            }
        } catch {
            message.error('AI failed to improve the description. Please try again.');
        } finally {
            setImprovingDescription(false);
        }
    };

    const handleUndoImproveDescription = () => {
        if (preImproveDescription !== null) {
            setEditDescription(preImproveDescription);
            setPreImproveDescription(null);
        }
    };

    const handleUpdateTask = async () => {
        if (!taskId) return;
        setUpdatingTask(true);
        try {
            const isMilestone = (task?.itemType === 'milestone') || (itemType === 'milestone');
            const estimatedTime = buildEstimatedTimeString(editEstHours, editEstMinutes);
            const actualTimeMsOverride = hmToMs(editEffortHours, editEffortMinutes);
            const payload = {
                title: editTitle,
                description: editDescription,
                priority: editPriority,
                itemType: isMilestone ? 'milestone' : 'task',
                milestoneOwner: milestoneOwner?._id || milestoneOwner || null,
                completionCriteria: completionCriteria || '',
                deliverables: deliverables || [],
                estimatedTime,
                actualTimeMs: actualTimeMsOverride,
            };

            const { data } = await updateTask(taskId, payload);
            const updated = data?.result || payload;

            setTask((prev) => ({ ...prev, ...updated }));
            if (typeof updated?.actualTimeMs === 'number') setActualTimeMs(updated.actualTimeMs);

            setEditTitle(updated?.title || editTitle);
            setEditDescription(updated?.description || editDescription);
            setEditPriority(updated?.priority || editPriority || 'Medium');

            const est = parseEstimatedTimeString(updated?.estimatedTime || estimatedTime || '');
            setEditEstHours(est.h);
            setEditEstMinutes(est.m);

            const effort = msToHM(typeof updated?.actualTimeMs === 'number' ? updated.actualTimeMs : actualTimeMsOverride);
            setEditEffortHours(effort.h);
            setEditEffortMinutes(effort.m);

            onTaskUpdated?.(data?.result || { ...(task || {}), ...payload });
            message.success('Task updated.');
            // Reset dirty snapshot to current saved values
            savedSnapshotRef.current = {
                title: editTitle,
                description: editDescription,
                priority: editPriority,
                estH: editEstHours,
                estM: editEstMinutes,
            };
        } catch {
            message.error('Failed to update task.');
        } finally {
            setUpdatingTask(false);
        }
    };

    const handleSaveDraft = async () => {
        const rawProjId = task?.project?._id || task?.project || (typeof selectedProject === 'object' ? selectedProject?._id : selectedProject) || null;
        const targetProjId = (rawProjId && rawProjId !== 'all') ? rawProjId : null;

        setUpdatingTask(true);
        try {
            const isMilestone = (task?.itemType === 'milestone') || (itemType === 'milestone');
            const estimatedTime = buildEstimatedTimeString(editEstHours, editEstMinutes);
            const draftPayload = {
                title: editTitle.trim() || 'Untitled Draft',
                description: editDescription?.trim() || '',
                project: targetProjId || undefined,
                itemType: isMilestone ? 'milestone' : 'task',
                milestoneOwner: milestoneOwner?._id || milestoneOwner || undefined,
                completionCriteria: completionCriteria?.trim() || '',
                deliverables: deliverables || [],
                priority: editPriority || 'Medium',
                status: 'Draft',
                isDraft: true,
                assignee: isMilestone ? undefined : (task?.assignee?._id || task?.assignee || undefined),
                collaborators: isMilestone ? [] : collaborators.map(c => c._id || c),
                reviewers: isMilestone ? [] : reviewers.map(r => r._id || r),
                additionalProjects: additionalProjects.map(p => p._id || p),
                startDate: task?.startDate || undefined,
                dueDate: task?.dueDate || undefined,
                estimatedTime: estimatedTime || undefined,
                parentId: (parentId && parentId !== 'new') ? parentId : undefined,
                milestoneId: milestoneId || initialMilestoneId || ((parentId && parentId !== 'new') ? parentId : undefined),
                billableStatus: task?.billableStatus || 'Billable',
                billingClearance: task?.billingClearance || 'Unbilled',
                category: task?.category || undefined,
                subCategory: task?.subCategory || undefined,
            };

            const isExistingDraft = Boolean(task?._id && task._id !== 'new' && (task.isDraft || task.status === 'Draft'));
            let createdDraft;
            if (isExistingDraft) {
                const { data } = await updateTask(task._id, draftPayload);
                createdDraft = data?.result || { ...task, ...draftPayload };
            } else {
                const { data } = await createTask(draftPayload);
                createdDraft = data?.result;
            }

            if (createdDraft?._id) {
                const { updatedDescription, uploadedAtts, descriptionChanged } = await uploadPendingAttachmentsForTask(
                    createdDraft._id,
                    editDescription?.trim() || createdDraft.description || ''
                );
                if (descriptionChanged) {
                    createdDraft.description = updatedDescription;
                }
                if (uploadedAtts.length > 0) {
                    createdDraft.attachments = [...(createdDraft.attachments || []), ...uploadedAtts];
                }
            }

            clearTaskDraft(targetProjId);
            if (createdDraft) {
                setTask((prev) => ({ ...(prev || {}), ...createdDraft, isDraft: true, status: 'Draft' }));
                savedSnapshotRef.current = {
                    title: createdDraft.title ?? editTitle,
                    description: createdDraft.description ?? editDescription,
                    priority: createdDraft.priority ?? editPriority,
                    estH: editEstHours,
                    estM: editEstMinutes,
                    assigneeId: createdDraft.assignee?._id || createdDraft.assignee || null,
                    projectId: createdDraft.project?._id || createdDraft.project || null,
                    milestoneId: milestoneId || null,
                    startDate: createdDraft.startDate || null,
                    dueDate: createdDraft.dueDate || null,
                };
                if (isExistingDraft) {
                    onTaskUpdated?.(createdDraft);
                } else {
                    onSuccess?.(createdDraft, { isDraft: true });
                }
            }
            message.success('Task saved as draft.');
            return true;
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to save draft.');
            return false;
        } finally {
            setUpdatingTask(false);
        }
    };

    /**
     * Guards the close action — prompts when creating a new task (mode === 'create')
     * or editing a draft task and user has entered meaningful inputs or changes.
     * When viewing an existing published task detail, closing never prompts.
     */
    const handleSafeClose = (closeFn) => {
        const hasInFlightUploads =
            isUploadingDescription ||
            pendingAttachments.some((f) => f.status === 'uploading') ||
            commentAttachments.some((f) => f.status === 'uploading') ||
            submittingComment;

        if (hasInFlightUploads) {
            Modal.confirm({
                title: 'Upload in Progress',
                icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
                content: 'Files are currently uploading. If you close the drawer now, the uploads will be interrupted. Do you want to close anyway?',
                okText: 'Close Anyway',
                okButtonProps: { danger: true },
                cancelText: 'Stay',
                onOk: () => {
                    closeFn?.();
                },
            });
            return;
        }

        const isDraft = Boolean(task?.isDraft || task?.status === 'Draft');
        if (mode !== 'create' && !isDraft) {
            closeFn?.();
            return;
        }

        const snap = savedSnapshotRef.current;
        if (!snap) { closeFn?.(); return; }

        const currentAssigneeId = task?.assignee?._id || task?.assignee || null;
        const currentProjId = task?.project?._id || task?.project || null;
        const currentMsId = milestoneId || task?.milestoneId?._id || task?.milestoneId || null;
        const plainDescription = (editDescription || '').replace(/<[^>]*>/g, '').trim();




        const hasInlineMedia = /<(img|video|a)\b/i.test(editDescription || '');
        const hasTypedDescription = !isQuillEmpty(editDescription) && (plainDescription.length > 0 || hasInlineMedia);

        const hasChanges =
            editTitle.trim() !== (snap.title || '').trim() ||
            hasTypedDescription ||
            editPriority !== (snap.priority || 'Medium') ||
            editEstHours !== (snap.estH || 0) ||
            editEstMinutes !== (snap.estM || 0) ||
            pendingAttachments.length > 0 ||
            String(currentAssigneeId || '') !== String(snap.assigneeId || '') ||
            String(currentProjId || '') !== String(snap.projectId || '') ||
            String(currentMsId || '') !== String(snap.milestoneId || '') ||
            Boolean(task?.dueDate) !== Boolean(snap.dueDate) ||
            Boolean(task?.startDate) !== Boolean(snap.startDate) ||
            (subtasks && subtasks.length > 0) ||
            (collaborators && collaborators.length > 0) ||
            (additionalProjects && additionalProjects.length > 0);

        if (!hasChanges) { closeFn?.(); return; }




        if (!editTitle.trim() && !hasTypedDescription && pendingAttachments.length === 0 &&
            String(currentAssigneeId || '') === String(snap.assigneeId || '') &&
            String(currentProjId || '') === String(snap.projectId || '') &&
            String(currentMsId || '') === String(snap.milestoneId || '') &&
            !task?.dueDate && !task?.startDate &&
            (!subtasks || subtasks.length === 0) &&
            (!collaborators || collaborators.length === 0) &&
            (!additionalProjects || additionalProjects.length === 0) &&
            (editEstHours || 0) === 0 && (editEstMinutes || 0) === 0
        ) {
            // Nothing meaningful entered — close without asking
            closeFn?.();
            return;
        }

        const modal = Modal.confirm({
            title: 'Save Draft or Discard Task?',
            content: 'You have entered information for this new task. Would you like to save it as a draft in Tasks to finish later, or discard it?',
            footer: () => (
                <div className="flex justify-end gap-2 mt-4">
                    <Button onClick={() => modal.destroy()}>
                        Keep Editing
                    </Button>
                    <Button
                        danger
                        onClick={() => {
                            const targetProjId = task?.project?._id || (typeof selectedProject === 'object' ? selectedProject?._id : selectedProject);
                            clearTaskDraft(targetProjId);
                            modal.destroy();
                            closeFn?.();
                        }}
                    >
                        Discard Task
                    </Button>
                    <Button
                        type="primary"
                        onClick={async () => {
                            modal.destroy();
                            const saved = await handleSaveDraft();
                            if (saved) {
                                closeFn?.();
                            }
                        }}
                    >
                        Save as Draft
                    </Button>
                </div>
            ),
        });
    };

    const handleAssigneeChange = async (newAssigneeId) => {
        const optimisticAssignee = newAssigneeId ? employees.find(e => String(e._id) === String(newAssigneeId)) : null;
        setTask((prev) => ({ ...prev, assignee: optimisticAssignee || (newAssigneeId ? { _id: newAssigneeId } : null) }));
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return; // don't hit API until task is saved
        const previous = task.assignee;
        setIsAutoSaving(true);
        try {
            const { data } = await updateTask(taskId, { assignee: newAssigneeId || null });
            setTask((prev) => ({ ...prev, assignee: data?.result?.assignee }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, assignee: previous }));
            message.error('Failed to update assignee.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const handleDueDateChange = async (date) => {
        const newDate = date ? date.toISOString() : null;
        setTask((prev) => ({ ...prev, dueDate: newDate }));
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        const previous = task.dueDate;
        setIsAutoSaving(true);
        try {
            const { data } = await updateTask(taskId, { dueDate: newDate });
            setTask((prev) => ({ ...prev, dueDate: data?.result?.dueDate }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, dueDate: previous }));
            message.error('Failed to update due date.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const handleStartDateChange = async (date) => {
        const newDate = date ? date.toISOString() : null;
        setTask((prev) => ({ ...prev, startDate: newDate }));
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        const previous = task.startDate;
        setIsAutoSaving(true);
        try {
            const { data } = await updateTask(taskId, { startDate: newDate });
            setTask((prev) => ({ ...prev, startDate: data?.result?.startDate }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, startDate: previous }));
            message.error('Failed to update start date.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const handleBillableStatusChange = async (newStatus) => {
        const previous = task?.billableStatus;
        setTask((prev) => ({ ...prev, billableStatus: newStatus }));
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const { data } = await updateTask(taskId, { billableStatus: newStatus });
            setTask((prev) => ({ ...prev, billableStatus: data?.result?.billableStatus || newStatus }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, billableStatus: previous }));
            message.error('Failed to update billable status.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const handleBillingClearanceChange = async (newClearance) => {
        const previous = task?.billingClearance;
        setTask((prev) => ({ ...prev, billingClearance: newClearance }));
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const { data } = await updateTask(taskId, { billingClearance: newClearance });
            setTask((prev) => ({ ...prev, billingClearance: data?.result?.billingClearance || newClearance }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, billingClearance: previous }));
            message.error('Failed to update billing clearance.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const handleCategoryChange = async (newCategory) => {
        const previous = task?.category;
        setTask((prev) => ({ ...prev, category: newCategory || null }));
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const { data } = await updateTask(taskId, { category: newCategory || null });
            setTask((prev) => ({ ...prev, category: data?.result?.category ?? (newCategory || null) }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, category: previous }));
            message.error('Failed to update category.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const handleSubCategoryChange = async (newSubCategory) => {
        const previous = task?.subCategory;
        setTask((prev) => ({ ...prev, subCategory: newSubCategory || null }));
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const { data } = await updateTask(taskId, { subCategory: newSubCategory || null });
            setTask((prev) => ({ ...prev, subCategory: data?.result?.subCategory ?? (newSubCategory || null) }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, subCategory: previous }));
            message.error('Failed to update sub-category.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const handleAddDependency = async (blockerTaskId, depType = 'finish_to_start') => {
        if (!blockerTaskId) return;
        const current = task.dependencies || [];
        if (current.some(d => String(d.taskId?._id || d.taskId) === String(blockerTaskId))) return;
        const newDep = { taskId: blockerTaskId, type: depType };
        const optimistic = [...current, newDep];
        setTask((prev) => ({ ...prev, dependencies: optimistic }));
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const { data } = await updateTask(taskId, {
                dependencies: optimistic.map(d => ({
                    taskId: d.taskId?._id || d.taskId,
                    type: d.type,
                })),
            });
            setTask((prev) => ({ ...prev, dependencies: data?.result?.dependencies || optimistic }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, dependencies: current }));
            message.error('Failed to add dependency.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const handleRemoveDependency = async (blockerTaskId) => {
        const current = task.dependencies || [];
        const updated = current.filter(d => String(d.taskId?._id || d.taskId) !== String(blockerTaskId));
        setTask((prev) => ({ ...prev, dependencies: updated }));
        if (mode === 'create' || Boolean(task?.isDraft) || task?.status === 'Draft') return;
        setIsAutoSaving(true);
        try {
            const { data } = await updateTask(taskId, {
                dependencies: updated.map(d => ({
                    taskId: d.taskId?._id || d.taskId,
                    type: d.type,
                })),
            });
            setTask((prev) => ({ ...prev, dependencies: data?.result?.dependencies || updated }));
            onTaskUpdated?.(data?.result);
        } catch {
            setTask((prev) => ({ ...prev, dependencies: current }));
            message.error('Failed to remove dependency.');
        } finally {
            setIsAutoSaving(false);
        }
    };

    const toggleProjectExpanded = (projId) => {
        setExpandedProjects((prev) => ({ ...prev, [projId]: !prev[projId] }));
    };

    const ownActiveAttendanceTask = attendance?.dailyTask?.find((dailyTask) => {
        const linkedTaskId = dailyTask?.projectTaskRef?._id || dailyTask?.projectTaskRef;
        return String(linkedTaskId || '') === String(task?._id || taskId || '');
    });
    const externalActiveAttendanceTask = Object.values(externalRunningTasks)[0];
    const activeAttendanceTask = ownActiveAttendanceTask || externalActiveAttendanceTask;
    const isTimerRunning = Boolean(activeAttendanceTask && (
        String(attendance?.activeTaskId || '') === String(activeAttendanceTask?._id || '')
        || Boolean(externalActiveAttendanceTask)
    ));
    const activeTaskDurationMs = parseDurationStringToMs(activeAttendanceTask?.duration);
    const runningSegmentMs = isTimerRunning ? getRunningTaskSegmentMs(activeAttendanceTask, liveTick) : 0;


    const displayActualTimeMs = actualTimeMs + runningSegmentMs;
    const estimatedMs = parseEstimatedMs(task?.estimatedTime);
    const remainingTimeMs = estimatedMs > 0 ? Math.max(0, estimatedMs - displayActualTimeMs) : null;
    const isOverrun = estimatedMs > 0 && displayActualTimeMs > estimatedMs;
    const completedSubtasks = (subtasks || []).filter((sub) => sub.status === 'Completed').length;
    const priorityStyle = PRIORITY_COLORS[task?.priority] || PRIORITY_COLORS.Medium;
    const isCompleted = task?.status === 'Completed';
    const collaboratorIds = new Set((collaborators || []).map((collaborator) => String(collaborator._id || collaborator)));
    const reviewerIds = new Set((reviewers || []).map((reviewer) => String(reviewer._id || reviewer)));
    const assigneeId = String(task?.assignee?._id || task?.assignee || '');
    const availableEmployees = (employees || []).filter((employee) => !collaboratorIds.has(String(employee._id)) && String(employee._id) !== assigneeId);
    const availableReviewers = (employees || []).filter((employee) => !reviewerIds.has(String(employee._id)) && String(employee._id) !== assigneeId);
    const addedProjectIds = new Set((additionalProjects || []).map((project) => String(project._id || project)));
    const primaryProjectId = String(task?.project?._id || task?.project || '');
    const availableProjects = (projects || []).filter((project) => !addedProjectIds.has(String(project._id)) && String(project._id) !== primaryProjectId);

    // Compute available milestones for the current primary project
    const targetProjectId = primaryProjectId || (selectedProject ? String(selectedProject._id || selectedProject) : '');
    const currentProj = (projects || []).find((p) => String(p._id) === String(targetProjectId)) || (typeof selectedProject === 'object' && String(selectedProject?._id) === String(targetProjectId) ? selectedProject : null);
    const embeddedMs = (currentProj?.milestones || []).map((m) => ({
        _id: String(m._id || m.id || m.name || m.title || ''),
        title: m.title || m.name || '',
        name: m.name || m.title || '',
        status: m.status || 'Backlog',
    })).filter((m) => m._id);

    const taskMs = (projectTasks || []).filter((t) => t.itemType === 'milestone' && String(t.project?._id || t.project) === String(targetProjectId)).map((t) => ({
        _id: String(t._id),
        title: t.title || t.name || '',
        name: t.title || t.name || '',
        status: t.status || 'Backlog',
    }));

    const milestoneMap = new Map();
    embeddedMs.forEach((m) => { if (m._id) milestoneMap.set(m._id, m); });
    taskMs.forEach((m) => { if (m._id) milestoneMap.set(m._id, m); });

    // Guarantee that if the task has a populated milestone (or parent milestone),
    // it is injected into milestoneMap so Ant Design Select always displays its label!
    const currentMsObj = (task?.milestoneId && typeof task.milestoneId === 'object')
        ? task.milestoneId
        : (task?.parentId && typeof task.parentId === 'object' && task.parentId.itemType === 'milestone')
            ? task.parentId
            : null;

    if (currentMsObj && (currentMsObj._id || currentMsObj.id)) {
        const msKey = String(currentMsObj._id || currentMsObj.id);
        if (!milestoneMap.has(msKey)) {
            milestoneMap.set(msKey, {
                _id: msKey,
                title: currentMsObj.title || currentMsObj.name || 'Current Milestone',
                name: currentMsObj.title || currentMsObj.name || 'Current Milestone',
                status: currentMsObj.status || 'In Progress',
            });
        }
    }

    const projectMilestones = Array.from(milestoneMap.values());

    // Expose safe arrays so consumers never get undefined
    const safeDependencies = task?.dependencies || [];
    const safeSubtasks = subtasks || [];
    const safeCollaborators = collaborators || [];
    const safeReviewers = reviewers || [];
    const safeAdditionalProjects = additionalProjects || [];
    const safeProjectTasks = projectTasks || [];

    const toggleBottomCollapsed = useCallback(() => {
        setIsBottomCollapsed((prev) => !prev);
    }, []);

    const toggleBottomExpanded = useCallback((e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        setIsBottomExpanded((prev) => {
            if (!prev) setIsBottomCollapsed(false);
            return !prev;
        });
    }, []);

    const jumpToComment = useCallback((targetCommentId) => {
        if (!targetCommentId) return;
        setIsBottomCollapsed(false);
        setActiveTab('comments');
        setTimeout(() => {
            const el = document.getElementById(`comment-${targetCommentId}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('ring-2', 'ring-blue-500', 'bg-blue-50/80', 'dark:bg-blue-950/70', 'transition-all', 'duration-500');
                setTimeout(() => {
                    el.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-50/80', 'dark:bg-blue-950/70');
                }, 3000);
            }
        }, 150);
    }, []);

    const jumpToAttachment = useCallback((targetAttachmentId) => {
        if (!targetAttachmentId) return;
        setIsBottomCollapsed(false);
        setActiveTab('attachments');
        setTimeout(() => {
            const el = document.getElementById(`attachment-${targetAttachmentId}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('ring-2', 'ring-blue-500', 'bg-blue-50/80', 'dark:bg-blue-950/70', 'transition-all', 'duration-500');
                setTimeout(() => {
                    el.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-50/80', 'dark:bg-blue-950/70');
                }, 3000);
            }
        }, 150);
    }, []);

    const contextValue = {
        taskId, open, onClose, closeAll, currentAdminId, currentAdmin,
        drawerStack, pushTask, popTask, popTo, isTop, hasMask,
        employees: employees || [],
        jumpToComment, jumpToAttachment,
        isBottomCollapsed, setIsBottomCollapsed, toggleBottomCollapsed,
        isBottomExpanded, setIsBottomExpanded, toggleBottomExpanded,
        projects: projects || [],
        task, setTask, loading, loadError, loadTask,
        actualTimeMs, displayActualTimeMs, remainingTimeMs,
        subtasks: safeSubtasks,
        collaborators: safeCollaborators,
        reviewers: safeReviewers,
        additionalProjects: safeAdditionalProjects,
        dependencies: safeDependencies,
        projectTasks: safeProjectTasks,
        milestoneId, setMilestoneId, projectMilestones, handleMilestoneChange,
        commentText, setCommentText, submittingComment, commentEndRef,
        commentAttachments, handleCommentFiles, removeCommentAttachment,
        commentFileReferences, setCommentFileReferences,
        commentMentions, setCommentMentions,
        isUploadingDescription, setIsUploadingDescription,
        pendingAttachments, setPendingAttachments, attachmentsDragOver, setAttachmentsDragOver,
        handleAttachmentTabFiles, handleAttachmentTabDrop,
        handleUploadPendingAttachments, removePendingAttachment,
        activeTab, setActiveTab,
        addingSubtask, setAddingSubtask, newSubtaskTitle, setNewSubtaskTitle, savingSubtask,
        statusUpdating, sharingLoading, shareUrl, setShareUrl,
        editTitle, setEditTitle, editDescription, setEditDescription, updatingTask,
        isAutoSaving, scheduleAutoSave,
        editPriority, setEditPriority, editEstHours, setEditEstHours, editEstMinutes, setEditEstMinutes, editEffortHours, setEditEffortHours, editEffortMinutes, setEditEffortMinutes,
        itemType, setItemType, milestoneOwner, setMilestoneOwner, completionCriteria, setCompletionCriteria, deliverables, setDeliverables, isMilestoneModalOpen, setIsMilestoneModalOpen,
        initialChildTasks, setInitialChildTasks, handleAddInitialChildTask, handleRemoveInitialChildTask,
        initialMilestoneTasks: initialChildTasks, handleAddInitialMilestoneTask, handleRemoveInitialMilestoneTask,
        improvingDescription, preImproveDescription,
        expandedProjects, toggleProjectExpanded,
        estimatedMs, isOverrun, completedSubtasks, priorityStyle, isCompleted,
        attendance, attendanceStatus, activeAttendanceTask, isTimerRunning,
        availableEmployees, availableReviewers, availableProjects,
        handleAddComment, handleDeleteComment, handleToggleCommentReaction, handleDeleteAttachment, handleBulkDeleteAttachments,
        handleToggleSubtask, handleOpenSubtask, handleAddSubtask, handleDeleteSubtask, handleSubtaskTitleEdit, handleSubtaskDragEnd,
        handleStatusChange, handleCompleteTask, handleShare, handleRevokeShare, copyInternalLink, handleStartTimer, handleStopTimer,
        handleAddCollaborator, handleRemoveCollaborator,
        handleAddReviewer, handleRemoveReviewer,
        handleAddProject, handleRemoveAdditionalProject,
        handleUpdateTask, handleSafeClose, handleAssigneeChange, handleDueDateChange, handleStartDateChange, handleBillableStatusChange, handleBillingClearanceChange, handleCategoryChange, handleSubCategoryChange,
        handleAddDependency, handleRemoveDependency,
        handleImproveDescription, handleUndoImproveDescription,
        mode, handleCreateTask, handleSaveDraft, handlePrimaryProjectChange,
    };

    return (
        <TaskDrawerContext.Provider value={contextValue}>
            {children}
        </TaskDrawerContext.Provider>
    );
}

export function useTaskDrawer() {
    const context = useContext(TaskDrawerContext);
    if (!context) {
        throw new Error('useTaskDrawer must be used within TaskDrawerProvider');
    }
    return context;
}

export default TaskDrawerContext;






















